/**
 * The Meta pixel, loaded only after the visitor accepts cookies.
 *
 * The same rule and the same cookie as the landing pages: nothing from Meta
 * loads before a yes, and every event the browser sends has a twin sent by
 * Pulse's server under the same id, so Meta counts it once.
 */

const COOKIE = "lw_consent";
const STANDARD = ["PageView", "ViewContent", "Lead", "Contact", "SubmitApplication", "CompleteRegistration",
  "Schedule", "InitiateCheckout", "Purchase", "Subscribe"];

type Fbq = ((...args: unknown[]) => void) & { callMethod?: (...a: unknown[]) => void; queue: unknown[];
  push: unknown; loaded: boolean; version: string };

declare global {
  interface Window {
    fbq?: Fbq;
    _fbq?: Fbq;
  }
}

let loaded = false;

export function consentChoice(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]*)`));
  return match ? match[1] : null;
}

/** Kept for the whole of lewagonindia.com, so the homepage and landing pages do not ask again. */
export function rememberChoice(value: "all" | "necessary") {
  const domain = /(^|\.)lewagonindia\.com$/.test(location.hostname) ? "; domain=lewagonindia.com" : "";
  document.cookie = `${COOKIE}=${value}; path=/; max-age=31536000; SameSite=Lax${domain}${location.protocol === "https:" ? "; Secure" : ""}`;
}

/** Meta's loader, as Meta publishes it, run once. */
export function loadPixel(pixelId: string, pageEventId?: string) {
  if (loaded) return;
  loaded = true;

  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as Fbq;
  if (!window.fbq) {
    window.fbq = fbq;
    window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }
  // No automatic button-click logging: the page sends the events it means.
  window.fbq!("set", "autoConfig", false, pixelId);
  window.fbq!("init", pixelId);
  window.fbq!("track", "PageView", {}, pageEventId ? { eventID: pageEventId } : undefined);
}

/** The browser's copy of an event the server has also sent, under the same id. */
export function trackMeta(name: string, data: Record<string, unknown>, eventId: string) {
  if (!loaded || !window.fbq) return;
  window.fbq(STANDARD.includes(name) ? "track" : "trackCustom", name, data, { eventID: eventId });
}

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let googleLoaded = false;

/** Google's tag, after consent only, with enhanced conversions allowed. */
export function loadGoogle(tag: string) {
  if (googleLoaded) return;
  googleLoaded = true;
  window.dataLayer = window.dataLayer || [];
  // gtag must push the arguments object itself, not an array copy of it.
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("consent", "default", { ad_storage: "granted", ad_user_data: "granted",
                                      ad_personalization: "granted", analytics_storage: "granted" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tag)}`;
  document.head.appendChild(script);
  window.gtag("js", new Date());
  window.gtag("config", tag, { allow_enhanced_conversions: true });
}

/** A conversion, with the typed email and phone, which Google hashes in the page before sending. */
export function convertGoogle(sendTo: string, user: { email?: string; phone?: string }, transactionId?: string) {
  if (!googleLoaded || !window.gtag) return;
  const data: Record<string, string> = {};
  if (user.email) data.email = user.email.toLowerCase();
  if (user.phone) data.phone_number = user.phone;
  if (Object.keys(data).length) window.gtag("set", "user_data", data);
  window.gtag("event", "conversion", { send_to: sendTo, transaction_id: transactionId });
}
